import React, { useCallback, useEffect, useState } from "react";
import { Alert, Box, Button, Card, CardContent, FormControlLabel, Stack, Switch, Typography } from "@mui/material";
import { SettingsSuggestOutlined } from "@mui/icons-material";

import CustomSnackBar from "../../Components/CustomSnackBar";
import MainText from "../../Components/MainText";
import { DEFAULT_SALES_CONFIG, hasAnyPermission, normalizeSalesConfig } from "../../config/permissionUtils";
import { useAuth } from "../../context/AuthContext";
import { invoiceService, saleService } from "../../services";

const METHOD_LABELS = {
  CASH: "Efectivo",
  TRANSFER: "Transferencia",
  VOUCHER: "Voucher",
};

const INVOICE_SETTINGS_DEFAULTS = {
  invoices_enabled: false,
  tickets_enabled: true,
  deferred_billing_enabled: false,
  branch_has_fiscal_data: false,
  missing_branch_fiscal_fields: [],
};

const normalizeInvoiceSettings = (data = {}) => ({
  invoices_enabled: Boolean(data?.invoices_enabled),
  tickets_enabled: data?.tickets_enabled !== false,
  deferred_billing_enabled: Boolean(data?.invoices_enabled && data?.deferred_billing_enabled),
  branch_has_fiscal_data: Boolean(data?.branch_has_fiscal_data),
  missing_branch_fiscal_fields: Array.isArray(data?.missing_branch_fiscal_fields)
    ? data.missing_branch_fiscal_fields
    : [],
});

const branchHasFiscalData = (branch) =>
  ["tax_nit", "tax_name", "tax_address", "establishment_code"].every((field) =>
    String(branch?.[field] || "").trim()
  );

function SalesConfigPage() {
  const { currentBranch, effectivePerms = [], isSuperuser, refreshSalesConfig } = useAuth();
  const branchId = currentBranch?.id || null;
  const canManage = hasAnyPermission(
    effectivePerms,
    ["manage_sales", "sale_manage_transfer_accounts", "cash_session_manage", "billing_manage"],
    isSuperuser
  );
  const [rows, setRows] = useState([]);
  const [salesConfig, setSalesConfig] = useState(DEFAULT_SALES_CONFIG);
  const [salesConfigLoading, setSalesConfigLoading] = useState(false);
  const [salesConfigSaving, setSalesConfigSaving] = useState(false);
  const [invoiceForm, setInvoiceForm] = useState(INVOICE_SETTINGS_DEFAULTS);
  const [invoiceLoading, setInvoiceLoading] = useState(false);
  const [invoiceSaving, setInvoiceSaving] = useState(false);
  const [snack, setSnack] = useState({ open: false, message: "", severity: "info" });

  const loadRows = useCallback(async () => {
    const data = await saleService.listPaymentMethodConfig();
    setRows(Array.isArray(data) ? data : []);
  }, []);

  const loadSalesConfig = useCallback(async () => {
    if (!branchId) {
      setSalesConfig(DEFAULT_SALES_CONFIG);
      return;
    }
    setSalesConfigLoading(true);
    try {
      const data = await saleService.getSalesConfig({ branch_id: branchId });
      setSalesConfig(normalizeSalesConfig(data, branchId));
    } catch {
      setSalesConfig(normalizeSalesConfig(DEFAULT_SALES_CONFIG, branchId));
      setSnack({ open: true, message: "No se pudo cargar la configuracion de ventas.", severity: "error" });
    } finally {
      setSalesConfigLoading(false);
    }
  }, [branchId]);

  const loadInvoiceSettings = useCallback(async () => {
    setInvoiceLoading(true);
    try {
      const data = await invoiceService.getSettings();
      setInvoiceForm(normalizeInvoiceSettings(data));
    } catch {
      setInvoiceForm(INVOICE_SETTINGS_DEFAULTS);
      setSnack({ open: true, message: "No se pudo cargar la configuración FEL.", severity: "error" });
    } finally {
      setInvoiceLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRows().catch(() => setRows([]));
    loadSalesConfig();
    loadInvoiceSettings();
  }, [loadInvoiceSettings, loadRows, loadSalesConfig]);

  useEffect(() => {
    if (salesConfig.allow_billing === true && salesConfig.allow_deferred_billing === true) return;
    setInvoiceForm((prev) =>
      prev.deferred_billing_enabled ? { ...prev, deferred_billing_enabled: false } : prev
    );
  }, [salesConfig.allow_billing, salesConfig.allow_deferred_billing]);

  const hasFiscalData = branchHasFiscalData(currentBranch) || invoiceForm.branch_has_fiscal_data;
  const billingModuleEnabled = salesConfig.allow_billing === true;
  const deferredBillingModuleEnabled =
    billingModuleEnabled && salesConfig.allow_deferred_billing === true;
  const invoiceDisabled = !canManage || invoiceLoading || invoiceSaving || !billingModuleEnabled;
  const invoiceSwitchDisabled = invoiceDisabled || !hasFiscalData;

  const updateMethod = async (row, isActive) => {
    if (!canManage || row?.code === "CASH") return;
    try {
      await saleService.updatePaymentMethodConfig({
        branch_id: branchId,
        code: row.code,
        is_active: isActive,
      });
      await loadRows();
      setSnack({ open: true, message: "Configuracion actualizada.", severity: "success" });
    } catch {
      setSnack({ open: true, message: "No se pudo actualizar el metodo.", severity: "error" });
    }
  };

  const updateSalesConfigField = async (field, value) => {
    if (!canManage || !branchId) return;
    const patch = { [field]: Boolean(value) };
    if (field === "allow_billing" && !value) {
      patch.allow_deferred_billing = false;
    }
    if (field === "allow_deferred_billing") {
      patch.allow_deferred_billing = Boolean(value && salesConfig.allow_billing);
    }

    setSalesConfig((prev) => normalizeSalesConfig({ ...prev, ...patch }, branchId));
    setSalesConfigSaving(true);
    try {
      const data = await saleService.updateSalesConfig({
        branch_id: branchId,
        ...patch,
      });
      setSalesConfig(normalizeSalesConfig(data, branchId));
      await refreshSalesConfig?.();
      setSnack({ open: true, message: "Configuracion de ventas actualizada.", severity: "success" });
    } catch (error) {
      await loadSalesConfig();
      const message = error?.response?.data?.detail || "No se pudo actualizar la configuracion de ventas.";
      setSnack({ open: true, message, severity: "error" });
    } finally {
      setSalesConfigSaving(false);
    }
  };

  const updateInvoiceField = (field, value) => {
    setInvoiceForm((prev) => {
      const next = { ...prev, [field]: value };
      if (field === "invoices_enabled" && !value) {
        next.deferred_billing_enabled = false;
      }
      if (field === "deferred_billing_enabled" && !deferredBillingModuleEnabled) {
        next.deferred_billing_enabled = false;
      }
      return next;
    });
  };

  const saveInvoiceSettings = async () => {
    if (!canManage) return;
    if (!billingModuleEnabled) {
      setSnack({
        open: true,
        message: "Primero debes habilitar facturacion.",
        severity: "warning",
      });
      return;
    }
    if (invoiceForm.invoices_enabled && !hasFiscalData) {
      setSnack({
        open: true,
        message: "Para habilitar emisión de facturas, complete los datos fiscales de esta sucursal.",
        severity: "error",
      });
      return;
    }
    if (!invoiceForm.tickets_enabled && !invoiceForm.invoices_enabled) {
      setSnack({ open: true, message: "Debes activar tickets o facturas.", severity: "error" });
      return;
    }
    setInvoiceSaving(true);
    try {
      const data = await invoiceService.updateSettings({
        invoices_enabled: invoiceForm.invoices_enabled,
        tickets_enabled: invoiceForm.tickets_enabled,
        deferred_billing_enabled:
          invoiceForm.invoices_enabled &&
          deferredBillingModuleEnabled &&
          invoiceForm.deferred_billing_enabled,
      });
      setInvoiceForm(normalizeInvoiceSettings(data));
      setSnack({ open: true, message: "Configuración FEL guardada.", severity: "success" });
    } catch (error) {
      const message =
        error?.response?.data?.detail ||
        error?.response?.data?.non_field_errors?.[0] ||
        "No se pudo guardar la configuración FEL.";
      setSnack({ open: true, message, severity: "error" });
    } finally {
      setInvoiceSaving(false);
    }
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <MainText
        title="Configuracion de ventas"
        subtitle="Metodos disponibles para cobro. Efectivo siempre permanece activo."
        icon={SettingsSuggestOutlined}
      />
      {!canManage ? (
        <Alert severity="info">Solo usuarios con permisos de ventas o caja pueden modificar.</Alert>
      ) : null}
      <Stack spacing={1.25}>
        <Card variant="outlined">
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Bandeja de caja de cobro
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Si esta activa, las ventas pueden enviarse a Caja para cobrarse despues. Si esta inactiva, las ventas se cobran directamente.
                </Typography>
              </Box>
              <FormControlLabel
                sx={{ m: 0 }}
                control={
                  <Switch
                    checked={salesConfig.allow_cashier_queue !== false}
                    disabled={!canManage || !branchId || salesConfigLoading || salesConfigSaving}
                    onChange={(event) =>
                      updateSalesConfigField("allow_cashier_queue", event.target.checked)
                    }
                  />
                }
                label="Permitir bandeja de caja de cobro"
              />
            </Stack>
          </CardContent>
        </Card>
        <Card variant="outlined">
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Stack spacing={1.25}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    Facturacion
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Controla si la sucursal puede acceder a vistas y operaciones de facturacion.
                  </Typography>
                </Box>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={billingModuleEnabled}
                      disabled={!canManage || !branchId || salesConfigLoading || salesConfigSaving}
                      onChange={(event) =>
                        updateSalesConfigField("allow_billing", event.target.checked)
                      }
                    />
                  }
                  label="Permitir facturacion"
                />
              </Stack>

              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Facturacion diferida
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {billingModuleEnabled
                      ? "Permite usar la bandeja de ventas pendientes de facturacion."
                      : "Primero debes habilitar facturacion."}
                  </Typography>
                </Box>
                <FormControlLabel
                  sx={{ m: 0 }}
                  control={
                    <Switch
                      checked={deferredBillingModuleEnabled}
                      disabled={
                        !canManage ||
                        !branchId ||
                        !billingModuleEnabled ||
                        salesConfigLoading ||
                        salesConfigSaving
                      }
                      onChange={(event) =>
                        updateSalesConfigField("allow_deferred_billing", event.target.checked)
                      }
                    />
                  }
                  label="Permitir facturacion diferida"
                />
              </Stack>
            </Stack>
          </CardContent>
        </Card>
        {rows.map((row) => (
          <Card key={row.code} variant="outlined">
            <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                    {METHOD_LABELS[row.code] || row.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {row.code === "CASH"
                      ? "Metodo predeterminado. No se puede desactivar."
                      : row.code === "TRANSFER"
                        ? "Controla cuentas destino, boleta y flujo de aprobacion."
                        : "Controla voucher/autorizacion en cobros."}
                  </Typography>
                </Box>
                <Switch
                  checked={row.code === "CASH" || row.is_active !== false}
                  disabled={!canManage || row.code === "CASH"}
                  onChange={(event) => updateMethod(row, event.target.checked)}
                />
              </Stack>
            </CardContent>
          </Card>
        ))}
        <Card variant="outlined">
          <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
            <Stack spacing={1.25}>
              <Box>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
                  Facturación FEL
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Configura el comportamiento de facturación electrónica para esta sucursal.
                </Typography>
              </Box>

              {!billingModuleEnabled ? (
                <Alert severity="info">Primero debes habilitar facturacion.</Alert>
              ) : null}

              <Stack spacing={0.75}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={invoiceForm.invoices_enabled}
                      onChange={(event) => updateInvoiceField("invoices_enabled", event.target.checked)}
                      disabled={invoiceSwitchDisabled}
                    />
                  }
                  label="Emisión de facturas"
                />
                {!hasFiscalData ? (
                  <Typography variant="body2" color="error">
                    Para habilitar emisión de facturas, complete los datos fiscales de esta sucursal.
                  </Typography>
                ) : null}
                <FormControlLabel
                  control={
                    <Switch
                      checked={invoiceForm.tickets_enabled}
                      onChange={(event) => updateInvoiceField("tickets_enabled", event.target.checked)}
                      disabled={invoiceDisabled}
                    />
                  }
                  label="Emisión de tickets"
                />
                {invoiceForm.invoices_enabled ? (
                  <Box>
                    <FormControlLabel
                      control={
                        <Switch
                          checked={invoiceForm.deferred_billing_enabled}
                          onChange={(event) => updateInvoiceField("deferred_billing_enabled", event.target.checked)}
                          disabled={invoiceDisabled || !deferredBillingModuleEnabled}
                        />
                      }
                      label="Facturación diferida"
                    />
                    <Typography variant="body2" color="text.secondary">
                      Envía las ventas a una bandeja de pendientes para facturarlas posteriormente.
                    </Typography>
                  </Box>
                ) : null}
              </Stack>

              <Stack direction="row" justifyContent="flex-end">
                <Button
                  variant="contained"
                  onClick={saveInvoiceSettings}
                  disabled={invoiceDisabled}
                >
                  {invoiceSaving ? "Guardando..." : "Guardar configuración FEL"}
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Stack>
      <CustomSnackBar
        open={snack.open}
        setOpen={(open) => setSnack((prev) => ({ ...prev, open }))}
        message={snack.message}
        severity={snack.severity}
      />
    </Box>
  );
}

export default SalesConfigPage;
